import * as React from 'react';
import Input, { InputProps } from '@material-ui/core/Input';
import uniqueId from 'lodash-es/uniqueId';
import styled, { css } from 'styled-components';
import { ForwardedFC } from '../types';
import { Label } from '../Form/Label';
import { HelperText, HelperTextProps, getHelperText } from '../Form/HelperText';
import { FormControl } from '../Form/FormControl';
import { Uds } from '../../types';
import { Typography } from '../Typography/Typography';

export type OcInputProps = Uds<
  InputProps & {
    label?: string;
    subLabel?: string;
    hasChips?: boolean;
    autoHeight?: boolean;
    helperText?: string | React.ReactNode;
    errorText?: string | React.ReactNode;
    helperTextProps?: HelperTextProps;
    formClassName?: string;
  }
>;

export const OcInput = React.forwardRef(
  (
    {
      autoHeight,
      label,
      subLabel,
      id,
      hasChips,
      helperText,
      errorText,
      dataTest,
      helperTextProps,
      formClassName,
      ...rest
    },
    ref
  ) => {
    const newId = id ?? uniqueId('ocInput') + Date.now();

    return (
      <FormControl className={formClassName} required={rest.required} error={rest.error} fullWidth={rest.fullWidth}>
        {!!label && (
          <Label shrink htmlFor={newId} required={rest.required}>
            {label}
          </Label>
        )}
        {!!subLabel && <Typography variant="body2">{subLabel}</Typography>}
        <StyledInput
          {...rest}
          data-component-role="input"
          data-test={dataTest}
          $hasChips={hasChips}
          $autoHeight={autoHeight || hasChips}
          id={newId}
          disableUnderline
          ref={ref}
        />
        {(!!helperText || !!errorText) && (
          <HelperText error={rest.error} {...helperTextProps}>
            {getHelperText(rest.error, helperText, errorText)}
          </HelperText>
        )}
      </FormControl>
    );
  }
) as ForwardedFC<OcInputProps, HTMLDivElement>;

const StyledInput = styled(Input)<{ $hasChips?: boolean; $autoHeight?: boolean }>`
  && {
    height: ${({ $autoHeight }) => ($autoHeight ? 'auto' : '36px')};
    ${({ $hasChips }) => ($hasChips ? hasChipsCss : undefined)}
  }
`;

const hasChipsCss = css`
  && {
    display: inline-flex;
    padding: 4px 56px 2px 8px; // now: should be: 34px
    position: relative;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    input {
      width: 0;
      display: flex;
      flex-grow: 1;
      flex-wrap: wrap;
      align-items: center;
      padding-left: 0;
      border-radius: 2px;
      text-overflow: ellipsis;
      justify-content: flex-start;
    }

    .MuiInputAdornment-positionStart {
      max-height: 150px;
      overflow: hidden auto;
      padding-left: 0;
    }

    .MuiInputAdornment-positionEnd {
      top: calc(50% - 14px);
      right: 0;
      position: absolute;
      height: auto;
    }
  }
`;

export default OcInput;
